import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner, Button, Card, Dialog, Field, FileDrop, Pill } from '@elysian/ui';
import type { ContactImportResult, ContactImportRow } from '../../data/source';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { KINDS, PACKAGES, readSageAccounting, type ImportKind, type ImportPackage } from './importFile';
import { ImportOpenItems } from './ImportOpenItems';

type Checked = { file: string; role: 'customer' | 'supplier'; rows: ContactImportRow[]; result: ContactImportResult; warning: string | null };
const readText = (f: File) => (typeof f.text === 'function' ? f.text()
  : new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsText(f); }));
const ACTION = { new: { tone: 'ok', label: 'New' }, update: { tone: 'info', label: 'Update' }, skip: { tone: 'draft', label: 'Skip' } } as const;

/**
 * Import data (#16): choose the package and what to import, pick the file,
 * see what each row will do, untick any to leave out, then confirm.
 * Customers and suppliers from Sage Accounting today; Sage 50, invoices and
 * purchases are listed but not ready yet.
 */
export function ImportContacts({ startWith, onClose, onStaged }: { startWith?: ImportKind; onClose: () => void; onStaged?: (msg: string) => void }) {
  const { source } = useSession();
  const toast = useToast((t) => t.show);
  const [pkg, setPkg] = useState<ImportPackage>('sage_accounting');
  const [kind, setKind] = useState<ImportKind>(startWith ?? 'customer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Checked | null>(null);
  const [leftOut, setLeftOut] = useState<Set<number>>(new Set());
  const [done, setDone] = useState<ContactImportResult | null>(null);
  const p = PACKAGES.find((x) => x.key === pkg)!;
  const k = KINDS.find((x) => x.key === kind)!;
  const ready = p.ready && k.ready;
  const role = kind === 'supplier' ? 'supplier' : 'customer';
  const plural = role === 'customer' ? 'customers' : 'suppliers';

  async function read(file: File) {
    setBusy(true);
    setError(null);
    try {
      const text = await readText(file);
      const r = readSageAccounting(text, role);
      if (r.problem) { setError(r.problem); return; }
      const result = await source.importContacts({ role, source: pkg as 'sage_accounting' | 'sage50', file_name: file.name, apply: false, rows: r.rows });
      setLeftOut(new Set());
      setChecked({ file: file.name, role, rows: r.rows, result, warning: r.warning });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!checked) return;
    setBusy(true);
    setError(null);
    try {
      const rows = checked.rows.filter((r) => !leftOut.has(r.row));
      const result = await source.importContacts({ role: checked.role, source: pkg as 'sage_accounting' | 'sage50', file_name: checked.file, apply: true, rows });
      setDone(result);
      toast(`${result.new} ${plural} added, ${result.update} updated`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Unpaid invoices and bills have their own steps (go-live item 9).
  if (kind === 'invoices' || kind === 'purchases') {
    return <ImportOpenItems pkg={pkg} kind={kind} onPkg={setPkg} onKind={setKind} onClose={onClose} onStaged={(m) => (onStaged ?? onClose)(m)} />;
  }

  // Step 1: what, from where, which file.
  if (!checked) {
    return (
      <Dialog title="Import data" onClose={onClose}
        actions={<Button variant="ghost" onClick={onClose}>Cancel</Button>}>
        <div className="el-form-grid">
          <Field id="imp-pkg" label="From">
            <select id="imp-pkg" className="el-input" value={pkg} onChange={(e) => setPkg(e.target.value as ImportPackage)}>
              {PACKAGES.map((x) => <option key={x.key} value={x.key} disabled={!x.ready}>{x.label}{x.ready ? '' : ' (coming soon)'}</option>)}
            </select>
          </Field>
          <Field id="imp-kind" label="What to import">
            <select id="imp-kind" className="el-input" value={kind} onChange={(e) => setKind(e.target.value as ImportKind)}>
              {KINDS.map((x) => <option key={x.key} value={x.key} disabled={!x.ready}>{x.label}</option>)}
            </select>
          </Field>
        </div>
        <p className="el-hint" style={{ margin: '0 0 10px' }}>{p.how}</p>
        {ready && (
          <FileDrop label={`Choose the ${plural} file`} accept=".csv,text/csv" disabled={busy}
            hint={busy ? 'Reading…' : 'Nothing is imported yet: you’ll see every row first.'}
            onFiles={(files) => files[0] && void read(files[0])} />
        )}
        {error && <div className="el-error-box" role="alert">{error}</div>}
      </Dialog>
    );
  }

  const r = checked.result;
  const taking = r.rows.filter((x) => x.action !== 'skip' && !leftOut.has(x.row));
  const toggle = (row: number, on: boolean) => setLeftOut((s) => { const n = new Set(s); if (on) n.delete(row); else n.add(row); return n; });

  // Done.
  if (done) {
    return (
      <Card as="section" padding="18px 20px" aria-labelledby="imp-done">
        <h2 id="imp-done" className="el-h2">Imported from {checked.file}</h2>
        <p style={{ margin: '6px 0 12px' }}>
          {done.new} {plural} added and {done.update} updated{done.skip ? `; ${done.skip} skipped` : ''}.
          Importing the same file again updates them rather than adding them twice.
        </p>
        <div className="el-row" style={{ gap: 12 }}>
          <Link className="el-btn" data-variant="primary" to={`/${plural}`}>See {plural}</Link>
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </div>
      </Card>
    );
  }

  // Step 2: review and confirm.
  return (
    <Card as="section" padding="18px 0 8px" aria-labelledby="imp-review">
      <div className="el-card-head el-txn-head">
        <div>
          <h2 id="imp-review" className="el-h2">Check the {plural} from {checked.file}</h2>
          <div className="el-hint">
            {r.new} new, {r.update} already here (updated){r.skip ? `, ${r.skip} skipped` : ''}. Untick any to leave out. Nothing is imported until you confirm.
          </div>
        </div>
        <div className="el-row" style={{ gap: 8 }}>
          <Button variant="ghost" disabled={busy} onClick={() => { setChecked(null); setError(null); }}>Choose another file</Button>
          <Button variant="primary" disabled={busy || taking.length === 0} onClick={() => void confirm()}>
            {busy ? 'Importing…' : `Import ${taking.length} ${taking.length === 1 ? role : plural}`}
          </Button>
        </div>
      </div>
      {checked.warning && <div style={{ padding: '0 16px' }}><Banner>{checked.warning}</Banner></div>}
      {error && <div style={{ padding: '0 16px' }}><div className="el-error-box" role="alert">{error}</div></div>}
      <div className="el-table-wrap">
        <table className="el-table" aria-label="Rows to import">
          <thead>
            <tr>
              <th scope="col" className="el-tick-col"><span className="el-vh">Include</span></th>
              <th scope="col">Row</th><th scope="col">Name</th><th scope="col">Will be</th><th scope="col">Code</th><th scope="col">Notes</th>
            </tr>
          </thead>
          <tbody>
            {r.rows.map((x) => (
              <tr key={x.row} className={x.action === 'skip' || leftOut.has(x.row) ? 'el-row-off' : undefined}>
                <td className="el-tick-col">
                  {x.action !== 'skip' && (
                    <input type="checkbox" aria-label={`Include row ${x.row}, ${x.name}`} checked={!leftOut.has(x.row)}
                      disabled={busy} onChange={(e) => toggle(x.row, e.target.checked)} />
                  )}
                </td>
                <td className="el-muted">{x.row}</td>
                <td>{x.name ?? <span className="el-muted">(no name)</span>}</td>
                <td><Pill tone={ACTION[x.action].tone}>{ACTION[x.action].label}</Pill></td>
                <td className="el-code">{x.code ?? <span className="el-muted">{x.action === 'new' ? 'Next' : '—'}</span>}</td>
                <td className="el-hint">{x.notes.join('. ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
